import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import i2Math from '@/utils/i2Math';
import { formatERPDate } from '@/utils/queryConfig';
import { renderFormattedInt } from '@/utils/renderUtils';
import { fetchDBSummary } from '@/redux/dashboard/dbSummary/dbSummaryTypeSlice';
import { fetchPackageSummary } from '@/redux/dashboard/getPackageSummary/packageSummarySlice';
import { fetchHomeServiceSummary } from '@/redux/dashboard/getHomeServiceSummary/homeServiceSummarySlice';
import { fetchERPgetStoreAnnualStatistics } from '@/redux/erp/erpgetStoreAnnualStatistics';
import { fetchERPStoreDataByDay } from '@/redux/erp/getERPStoreDataByDay';

function BusinessData({ storeId }) {
  const dispatch = useDispatch();
  const app = useSelector((state) => state.app);
  const dashboard = useSelector((state) => state.dashboard);
  const erp = useSelector((state) => state.erp);

  const [hoveredBar, setHoveredBar] = useState(null);
  const [hoveredLinePoint, setHoveredLinePoint] = useState(null);
  const [hoveredLinePoint2, setHoveredLinePoint2] = useState(null);
  const [barTipPos, setBarTipPos] = useState({ x: 0, y: 0 });
  const [lineTipPos1, setLineTipPos1] = useState({ x: 0, y: 0 });
  const [lineTipPos2, setLineTipPos2] = useState({ x: 0, y: 0 });
  const [portalRoot, setPortalRoot] = useState(null);

  const selectedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';
  const user = app?.user || {};
  const today = dayjs().format('YYYY-MM-DD');
  const yearNow = String(dayjs().year());
  const yearPrev = String(dayjs().year() - 1);

  useEffect(() => {
    setPortalRoot(document.body);
  }, []);

  useEffect(() => {
    if (!selectedStoreId) return undefined;
    const pyQuery = { store_id: selectedStoreId, date: today };
    dispatch(fetchDBSummary({ query: pyQuery })).unwrap().catch(() => {});
    dispatch(fetchPackageSummary({ query: pyQuery })).unwrap().catch(() => {});
    dispatch(fetchHomeServiceSummary({ query: pyQuery })).unwrap().catch(() => {});

    const userId = user.oa_id;
    const companyId = user.com_id;
    if (userId != null && userId !== '' && companyId != null && companyId !== '') {
      dispatch(fetchERPgetStoreAnnualStatistics({
        serviceType: 'all',
        query: { userId, companyId, year: yearNow, projectName: 'all' }
      })).unwrap().catch(() => {});
      dispatch(fetchERPgetStoreAnnualStatistics({
        serviceType: 'package',
        query: { userId, companyId, year: yearNow, projectName: '销售业绩' }
      })).unwrap().catch(() => {});
      dispatch(fetchERPgetStoreAnnualStatistics({
        serviceType: 'package',
        query: { userId, companyId, year: yearPrev, projectName: '销售业绩', writeMainSlot: false }
      })).unwrap().catch(() => {});
      dispatch(fetchERPgetStoreAnnualStatistics({
        serviceType: 'postpartum',
        query: { userId, companyId, year: yearNow, projectName: '产后复原' }
      })).unwrap().catch(() => {});
      dispatch(fetchERPStoreDataByDay({
        query: { date: formatERPDate(today, 'day') }
      })).unwrap().catch(() => {});
    }
    return undefined;
  }, [dispatch, selectedStoreId, today, yearNow, yearPrev, user.oa_id, user.com_id]);

  const view = useMemo(() => {
    const emptyYear = [];
    for (let i = 1; i <= 12; i += 1) {
      emptyYear.push({ label: `${String(i).padStart(2, '0')}月`, value: 0 });
    }
    const emptyRolling = [];
    for (let i = 11; i >= 0; i -= 1) {
      const d = dayjs().subtract(i, 'month');
      emptyRolling.push({
        label: `${String(d.year()).slice(2)}年${d.month() + 1}月`,
        value: 0
      });
    }
    const blank = {
      storeTotal: 0,
      customerCount: '',
      monthlyData: emptyYear,
      todayPackageAmount: 0,
      todayPackageCount: '',
      monthPackageAmount: 0,
      monthPackageCount: '',
      todayPostpartumAmount: 0,
      todayPostpartumCount: '',
      monthPostpartumAmount: 0,
      monthPostpartumCount: '',
      monthlyPackageData: emptyRolling,
      packageTotal: 0,
      yearlyProduceData: emptyYear,
      postpartumTotal: 0
    };
    if (!selectedStoreId) return blank;

    const dbSummary = dashboard?.dbSummary || {};
    const packageSummary = dashboard?.packageSummary || {};
    const homeServiceSummary = dashboard?.homeServiceSummary || {};
    const erpAnnual = erp?.erpgetStoreAnnualStatistics || {};
    const erpByDay = erp?.erpStoreDataByDay || {};

    if (String(dbSummary.query?.store_id ?? '') === selectedStoreId) {
      const raw = dbSummary.data?.totalCustomerCount;
      blank.customerCount = raw == null || raw === '' ? '' : raw;
    }
    if (String(packageSummary.query?.store_id ?? '') === selectedStoreId) {
      const d = packageSummary.data || {};
      blank.todayPackageCount = d.todaySignedOrderCount;
      blank.monthPackageCount = d.monthSignedOrderCount;
    }
    if (String(homeServiceSummary.query?.store_id ?? '') === selectedStoreId) {
      const d = homeServiceSummary.data || {};
      blank.todayPostpartumCount = d.todayHomeServiceCount;
      blank.monthPostpartumCount = d.monthHomeServiceCount;
    }

    const allSlot = erpAnnual.all;
    if (allSlot && String(allSlot.query?.year ?? '') === yearNow) {
      const rows = Array.isArray(allSlot.store_list) ? allSlot.store_list : [];
      let storeRow = null;
      for (let i = 0; i < rows.length; i += 1) {
        if (rows[i] && String(rows[i].id) === selectedStoreId) {
          storeRow = rows[i];
          break;
        }
      }
      if (storeRow) {
        blank.storeTotal = storeRow.total;
        const monthVals = Array.from({ length: 12 }, () => 0);
        const list = Array.isArray(storeRow.list) ? storeRow.list : [];
        for (let i = 0; i < list.length; i += 1) {
          const m = parseInt(String(list[i].month), 10);
          if (!Number.isFinite(m) || m < 1 || m > 12) continue;
          monthVals[m - 1] = Number(list[i].sum) || 0;
        }
        blank.monthlyData = monthVals.map((value, i) => ({
          label: `${String(i + 1).padStart(2, '0')}月`,
          value
        }));
      }
    }

    const thisMonth = dayjs().month() + 1;
    const pkgSlot = erpAnnual.package;
    if (pkgSlot && String(pkgSlot.query?.year ?? '') === yearNow) {
      const rows = Array.isArray(pkgSlot.store_list) ? pkgSlot.store_list : [];
      let storeRow = null;
      for (let i = 0; i < rows.length; i += 1) {
        if (rows[i] && String(rows[i].id) === selectedStoreId) {
          storeRow = rows[i];
          break;
        }
      }
      if (storeRow) {
        const list = Array.isArray(storeRow.list) ? storeRow.list : [];
        for (let i = 0; i < list.length; i += 1) {
          if (parseInt(String(list[i].month), 10) === thisMonth) {
            blank.monthPackageAmount = Number(list[i].sum) || 0;
            break;
          }
        }
      }
    }

    const ppSlot = erpAnnual.postpartum;
    if (ppSlot && String(ppSlot.query?.year ?? '') === yearNow) {
      const rows = Array.isArray(ppSlot.store_list) ? ppSlot.store_list : [];
      let storeRow = null;
      for (let i = 0; i < rows.length; i += 1) {
        if (rows[i] && String(rows[i].id) === selectedStoreId) {
          storeRow = rows[i];
          break;
        }
      }
      if (storeRow) {
        blank.postpartumTotal = storeRow.total;
        const monthVals = Array.from({ length: 12 }, () => 0);
        const list = Array.isArray(storeRow.list) ? storeRow.list : [];
        for (let i = 0; i < list.length; i += 1) {
          const m = parseInt(String(list[i].month), 10);
          if (!Number.isFinite(m) || m < 1 || m > 12) continue;
          monthVals[m - 1] = Number(list[i].sum) || 0;
          if (m === thisMonth) blank.monthPostpartumAmount = Number(list[i].sum) || 0;
        }
        blank.yearlyProduceData = monthVals.map((value, i) => ({
          label: `${String(i + 1).padStart(2, '0')}月`,
          value
        }));
      }
    }

    const byDayDate = formatERPDate(today, 'day');
    if (String(erpByDay.query?.date ?? '') === byDayDate) {
      const rows = Array.isArray(erpByDay.data?.list) ? erpByDay.data.list : [];
      let storeRow = null;
      for (let i = 0; i < rows.length; i += 1) {
        if (rows[i] && String(rows[i].id) === selectedStoreId) {
          storeRow = rows[i];
          break;
        }
      }
      if (storeRow) {
        blank.todayPackageAmount = Number(storeRow.sales) || 0;
        blank.todayPostpartumAmount = Number(storeRow.health) || 0;
      }
    }

    const yearMonthToSum = new Map();
    const byYear = erpAnnual.packageByYear || {};
    const yearKeys = [yearPrev, yearNow];
    for (let y = 0; y < yearKeys.length; y += 1) {
      const pkg = byYear[yearKeys[y]];
      if (!pkg) continue;
      const rows = Array.isArray(pkg.store_list) ? pkg.store_list : [];
      let storeRow = null;
      for (let i = 0; i < rows.length; i += 1) {
        if (rows[i] && String(rows[i].id) === selectedStoreId) {
          storeRow = rows[i];
          break;
        }
      }
      if (!storeRow || !Array.isArray(storeRow.list)) continue;
      for (let i = 0; i < storeRow.list.length; i += 1) {
        const m = parseInt(String(storeRow.list[i].month), 10);
        if (!Number.isFinite(m) || m < 1 || m > 12) continue;
        yearMonthToSum.set(`${yearKeys[y]}-${m}`, Number(storeRow.list[i].sum) || 0);
      }
    }
    const rolling = [];
    let rollingTotal = 0;
    for (let i = 11; i >= 0; i -= 1) {
      const d = dayjs().subtract(i, 'month');
      const y = d.year();
      const mo = d.month() + 1;
      const value = yearMonthToSum.get(`${y}-${mo}`) ?? 0;
      rollingTotal = i2Math.i2Add(rollingTotal, value);
      rolling.push({
        label: `${String(y).slice(2)}年${mo}月`,
        value
      });
    }
    if (yearMonthToSum.size > 0) {
      blank.monthlyPackageData = rolling;
      blank.packageTotal = rollingTotal;
    }

    return blank;
  }, [dashboard, erp, selectedStoreId, today, yearNow, yearPrev]);

  const monthlyData = view.monthlyData;
  const monthlyPackageData = view.monthlyPackageData;
  const yearlyProduceData = view.yearlyProduceData;

  const maxMonthly = Math.max(...monthlyData.map((d) => d.value), 0) || 1;
  const maxPackage = Math.max(...monthlyPackageData.map((d) => d.value), 0) || 1;
  const maxYearly = Math.max(...yearlyProduceData.map((d) => d.value), 0) || 1;

  const formatNum = (num) => {
    if (num >= 10000) {
      return `${(num / 10000).toFixed(1)}万`;
    }
    return Number(num).toLocaleString();
  };

  const formatValue = (num) => `${Number(num).toLocaleString()} 元`;

  const yLabels = (maxVal) => [
    formatNum(maxVal),
    formatNum(maxVal * 0.75),
    formatNum(maxVal * 0.5),
    formatNum(maxVal * 0.25)
  ];

  const countText = (raw) => {
    if (raw == null || raw === '') return '--';
    const n = Number(raw);
    return Number.isFinite(n) ? String(n) : String(raw);
  };

  const createLinePath = (data, maxVal, width, height, padding = { top: 10, right: 10, bottom: 20, left: 35 }) => {
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    const last = data.length > 1 ? data.length - 1 : 1;
    return data.map((d, i) => {
      const x = padding.left + (i / last) * chartWidth;
      const y = padding.top + chartHeight - (d.value / maxVal) * chartHeight;
      return `${x},${y}`;
    }).join(' ');
  };

  const createAreaPath = (data, maxVal, width, height, padding = { top: 10, right: 10, bottom: 20, left: 35 }) => {
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    const last = data.length > 1 ? data.length - 1 : 1;
    const points = data.map((d, i) => {
      const x = padding.left + (i / last) * chartWidth;
      const y = padding.top + chartHeight - (d.value / maxVal) * chartHeight;
      return `${x},${y}`;
    });
    return `M${padding.left},${padding.top + chartHeight} L${points.join(' L')} L${padding.left + chartWidth},${padding.top + chartHeight} Z`;
  };

  const getTooltipPos = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top - 20 };
  };

  const tooltipStyle = (pos) => ({
    position: 'fixed',
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    transform: 'translate(-50%, -100%)',
    pointerEvents: 'none',
    zIndex: 9999
  });

  const barY = yLabels(maxMonthly);
  const pkgY = [
    formatNum(maxPackage),
    formatNum(maxPackage * 0.8),
    formatNum(maxPackage * 0.6),
    formatNum(maxPackage * 0.4),
    formatNum(maxPackage * 0.2),
    '0'
  ];
  const prodY = [
    formatNum(maxYearly),
    formatNum(maxYearly * 0.8),
    formatNum(maxYearly * 0.6),
    formatNum(maxYearly * 0.4),
    formatNum(maxYearly * 0.2),
    '0'
  ];

  return (
    <div className="business-data">
      <div className="bd-section bd-top">
        <div className="bd-corner-bl"></div>
        <div className="bd-corner-tr"></div>
        <div className="bd-side-left"></div>
        <div className="bd-side-right"></div>
        <div className="bd-stats">
          <div className="bd-stat">
            <div className="bd-stat-label">门店总金额</div>
            <div className="bd-stat-value">{renderFormattedInt(view.storeTotal)}</div>
            <div className="bd-stat-unit">元</div>
          </div>
          <div className="bd-stat">
            <div className="bd-stat-label">累计服务客户</div>
            <div className="bd-stat-value small">{countText(view.customerCount)}</div>
            <div className="bd-stat-unit">人</div>
          </div>
        </div>
        <div className="bd-monthly-chart">
          <svg viewBox="0 0 200 60" className="bar-svg">
            <defs>
              <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00e5ff" stopOpacity="1" />
                <stop offset="100%" stopColor="#00e5ff" stopOpacity="0.7" />
              </linearGradient>
              <linearGradient id="barGradHover" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00e5ff" stopOpacity="1" />
                <stop offset="100%" stopColor="#00e5ff" stopOpacity="0.8" />
              </linearGradient>
            </defs>
            {monthlyData.map((d, i) => {
              const barHeight = (d.value / maxMonthly) * 40;
              const x = 25 + i * 15;
              const y = 52 - barHeight;
              const isHovered = hoveredBar === i;
              return (
                <g key={i}
                   onMouseEnter={(e) => {
                     setHoveredBar(i);
                     setBarTipPos(getTooltipPos(e));
                   }}
                   onMouseLeave={() => setHoveredBar(null)}
                   style={{ cursor: 'pointer' }}
                >
                  <rect
                    x={x - 1}
                    y={y - 2}
                    width="12"
                    height={barHeight + 2}
                    fill="transparent"
                  />
                  <rect
                    x={x}
                    y={y}
                    width={isHovered ? "11" : "10"}
                    height={barHeight}
                    fill={isHovered ? "url(#barGradHover)" : "url(#barGrad)"}
                    rx="1"
                    className="bd-bar-rect"
                    style={{
                      animationDelay: `${i * 0.08}s`,
                      transition: 'all 0.2s ease',
                      filter: isHovered ? 'drop-shadow(0 0 6px rgba(0,229,255,0.8))' : 'none'
                    }}
                  />
                  <text x={x + 5} y="58" textAnchor="middle" fill="rgba(230,247,255,0.5)" fontSize="4">
                    {d.label}
                  </text>
                  {isHovered && (
                    <g>
                      <line x1={x + 5} y1={y} x2={x + 5} y2={5}
                            stroke="#00e5ff" strokeWidth="0.5" strokeDasharray="2,1" opacity="0.6" />
                      <text x={x + 5} y={y - 3} textAnchor="middle" fill="#00e5ff" fontSize="6" fontWeight="bold">
                        {formatNum(d.value)}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </svg>
          <div className="bd-y-axis">
            {barY.map((t, i) => <span key={i}>{t}</span>)}
          </div>
        </div>
      </div>

      <div className="bd-section bd-middle">
        <div className="bd-row">
          <div className="bd-item">
            <div className="bd-item-label">日套餐签单</div>
            <div className="bd-item-value">{renderFormattedInt(view.todayPackageAmount)}</div>
            <div className="bd-item-unit">元</div>
            <div className="bd-item-count">{countText(view.todayPackageCount)} 单</div>
          </div>
          <div className="bd-divider"></div>
          <div className="bd-item">
            <div className="bd-item-label">月套餐签单</div>
            <div className="bd-item-value">{renderFormattedInt(view.monthPackageAmount)}</div>
            <div className="bd-item-unit">元</div>
            <div className="bd-item-count">{countText(view.monthPackageCount)} 单</div>
          </div>
        </div>
        <div className="bd-row">
          <div className="bd-item">
            <div className="bd-item-label">日产康签单</div>
            <div className="bd-item-value">{renderFormattedInt(view.todayPostpartumAmount)}</div>
            <div className="bd-item-unit">元</div>
            <div className="bd-item-count">{countText(view.todayPostpartumCount)} 单</div>
          </div>
          <div className="bd-divider"></div>
          <div className="bd-item">
            <div className="bd-item-label">月产康签单</div>
            <div className="bd-item-value">{renderFormattedInt(view.monthPostpartumAmount)}</div>
            <div className="bd-item-unit">元</div>
            <div className="bd-item-count">{countText(view.monthPostpartumCount)} 单</div>
          </div>
        </div>
      </div>

      <div className="bd-section bd-bottom">
        <div className="bd-chart">
          <div className="bd-chart-header">近12月月子套餐签单金额</div>
          <svg viewBox="0 0 340 105" className="line-svg">
            <defs>
              <linearGradient id="areaGrad1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00e5ff" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#00e5ff" stopOpacity="0" />
              </linearGradient>
              <filter id="glowFilter">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            {[0, 1, 2, 3, 4].map((i) => (
              <line key={i} x1="35" y1={10 + i * 20} x2="335" y2={10 + i * 20}
                    stroke="rgba(0,229,255,0.1)" strokeWidth="0.5" />
            ))}
            {pkgY.map((t, i) => (
              <text key={t} x="30" y={14 + i * 20} textAnchor="end" fill="rgba(230,247,255,0.5)" fontSize="7">{t}</text>
            ))}

            <path d={createAreaPath(monthlyPackageData, maxPackage, 340, 105, { top: 10, right: 5, bottom: 10, left: 35 })} fill="url(#areaGrad1)" className="bd-line-area bd-line-area-1" />
            <polyline
              points={createLinePath(monthlyPackageData, maxPackage, 340, 105, { top: 10, right: 5, bottom: 10, left: 35 })}
              fill="none"
              stroke="#00e5ff"
              strokeWidth="1.5"
              className="bd-line bd-line-1"
            />
            {hoveredLinePoint !== null && (() => {
              const padding = { top: 10, right: 5, bottom: 10, left: 35 };
              const chartWidth = 340 - padding.left - padding.right;
              const last = monthlyPackageData.length > 1 ? monthlyPackageData.length - 1 : 1;
              const x = padding.left + (hoveredLinePoint / last) * chartWidth;
              return (
                <line
                  x1={x} y1={10} x2={x} y2={110}
                  stroke="#00e5ff"
                  strokeWidth="0.5"
                  strokeDasharray="2,1"
                  opacity="0.5"
                />
              );
            })()}
            {monthlyPackageData.map((d, i) => {
              const padding = { top: 10, right: 5, bottom: 10, left: 35 };
              const chartWidth = 340 - padding.left - padding.right;
              const chartHeight = 105 - padding.top - padding.bottom;
              const last = monthlyPackageData.length > 1 ? monthlyPackageData.length - 1 : 1;
              const x = padding.left + (i / last) * chartWidth;
              const y = padding.top + chartHeight - (d.value / maxPackage) * chartHeight;
              const isHovered = hoveredLinePoint === i;
              return (
                <g key={i}
                   onMouseEnter={(e) => {
                     setHoveredLinePoint(i);
                     setLineTipPos1(getTooltipPos(e));
                   }}
                   onMouseLeave={() => setHoveredLinePoint(null)}
                   style={{ cursor: 'pointer' }}
                >
                  <circle cx={x} cy={y} r="8" fill="transparent" />
                  <circle
                    cx={x}
                    cy={y}
                    r={isHovered ? "4" : "2"}
                    fill="#00e5ff"
                    stroke={isHovered ? "#ffffff" : "none"}
                    strokeWidth="1"
                    style={{
                      filter: isHovered ? 'url(#glowFilter)' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                    className="bd-line-dot bd-line-dot-1"
                  />
                  {isHovered && (
                    <g>
                      <rect x={x - 18} y={y - 18} width="36" height="12"
                        fill="rgba(0, 20, 40, 0.9)" stroke="#00e5ff" strokeWidth="0.5" rx="1" />
                      <text x={x} y={y - 9} textAnchor="middle" fill="#00e5ff" fontSize="7" fontWeight="bold">
                        {formatNum(d.value)}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
            {monthlyPackageData.map((d, i) => {
              const padding = { top: 10, right: 5, bottom: 10, left: 35 };
              const chartWidth = 340 - padding.left - padding.right;
              const last = monthlyPackageData.length > 1 ? monthlyPackageData.length - 1 : 1;
              const x = padding.left + (i / last) * chartWidth;
              return (
                <text key={i} x={x} y="118" textAnchor="middle"
                      fill={hoveredLinePoint === i ? '#00e5ff' : 'rgba(230,247,255,0.5)'}
                      fontSize="6"
                      style={{ transition: 'fill 0.2s' }}>
                  {d.label}
                </text>
              );
            })}
          </svg>
          <div className="bd-chart-total">
            <span>总计</span>
            <span className="bd-total-value">{renderFormattedInt(view.packageTotal)} 元</span>
          </div>
        </div>
        <div className="bd-chart">
          <div className="bd-chart-header">年度产康签单金额</div>
          <svg viewBox="0 0 200 105" className="line-svg">
            <defs>
              <linearGradient id="areaGrad2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00d4aa" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#00d4aa" stopOpacity="0" />
              </linearGradient>
              <filter id="glowFilter2">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            {[0, 1, 2, 3, 4].map((i) => (
              <line key={i} x1="30" y1={10 + i * 20} x2="195" y2={10 + i * 20}
                    stroke="rgba(0,212,170,0.1)" strokeWidth="0.5" />
            ))}
            {prodY.map((t, i) => (
              <text key={`${t}-${i}`} x="25" y={14 + i * 20} textAnchor="end" fill="rgba(230,247,255,0.5)" fontSize="6">{t}</text>
            ))}

            <path d={createAreaPath(yearlyProduceData, maxYearly, 200, 105, { top: 10, right: 5, bottom: 20, left: 30 })} fill="url(#areaGrad2)" className="bd-line-area bd-line-area-2" />
            <polyline
              points={createLinePath(yearlyProduceData, maxYearly, 200, 105, { top: 10, right: 5, bottom: 20, left: 30 })}
              fill="none"
              stroke="#00d4aa"
              strokeWidth="1.5"
              className="bd-line bd-line-2"
            />
            {hoveredLinePoint2 !== null && yearlyProduceData.length > 0 && (
              <line
                x1={30 + (hoveredLinePoint2 / (yearlyProduceData.length > 1 ? yearlyProduceData.length - 1 : 1)) * 165}
                y1={10}
                x2={30 + (hoveredLinePoint2 / (yearlyProduceData.length > 1 ? yearlyProduceData.length - 1 : 1)) * 165}
                y2={110}
                stroke="#00d4aa"
                strokeWidth="0.5"
                strokeDasharray="2,1"
                opacity="0.5"
              />
            )}
            {yearlyProduceData.map((d, i) => {
              const padding = { top: 10, right: 5, bottom: 20, left: 30 };
              const chartWidth = 200 - padding.left - padding.right;
              const chartHeight = 105 - padding.top - padding.bottom;
              const last = yearlyProduceData.length > 1 ? yearlyProduceData.length - 1 : 1;
              const x = padding.left + (i / last) * chartWidth;
              const y = padding.top + chartHeight - (d.value / maxYearly) * chartHeight;
              const isHovered = hoveredLinePoint2 === i;
              return (
                <g key={i}
                   onMouseEnter={(e) => {
                     setHoveredLinePoint2(i);
                     setLineTipPos2(getTooltipPos(e));
                   }}
                   onMouseLeave={() => setHoveredLinePoint2(null)}
                   style={{ cursor: 'pointer' }}
                >
                  <circle cx={x} cy={y} r="6" fill="transparent" />
                  <circle
                    cx={x}
                    cy={y}
                    r={isHovered ? "4" : "2"}
                    fill="#00d4aa"
                    stroke={isHovered ? "#ffffff" : "none"}
                    strokeWidth="1"
                    style={{
                      filter: isHovered ? 'url(#glowFilter2)' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                    className="bd-line-dot bd-line-dot-2"
                  />
                  {isHovered && (
                    <g>
                      <rect x={x - 20} y={y - 18} width="40" height="12"
                        fill="rgba(0, 20, 40, 0.9)" stroke="#00d4aa" strokeWidth="0.5" rx="1" />
                      <text x={x} y={y - 9} textAnchor="middle" fill="#00d4aa" fontSize="7" fontWeight="bold">
                        {formatNum(d.value)}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
            {yearlyProduceData.map((d, i) => {
              const padding = { top: 10, right: 5, bottom: 20, left: 30 };
              const chartWidth = 200 - padding.left - padding.right;
              const last = yearlyProduceData.length > 1 ? yearlyProduceData.length - 1 : 1;
              const x = padding.left + (i / last) * chartWidth;
              return (
                <text key={i} x={x} y="118" textAnchor="middle"
                      fill={hoveredLinePoint2 === i ? '#00d4aa' : 'rgba(230,247,255,0.5)'}
                      fontSize="5"
                      style={{ transition: 'fill 0.2s' }}>
                  {d.label}
                </text>
              );
            })}
          </svg>
          <div className="bd-chart-total">
            <span>总计</span>
            <span className="bd-total-value green">{renderFormattedInt(view.postpartumTotal)} 元</span>
          </div>
        </div>
      </div>

      {portalRoot && hoveredBar !== null && monthlyData[hoveredBar] && createPortal(
        <div className="bd-bar-tooltip" style={tooltipStyle(barTipPos)}>
          <div className="bd-tooltip-month">{monthlyData[hoveredBar].label}</div>
          <div className="bd-tooltip-value">{formatValue(monthlyData[hoveredBar].value)}</div>
        </div>,
        portalRoot
      )}
      {portalRoot && hoveredLinePoint !== null && monthlyPackageData[hoveredLinePoint] && createPortal(
        <div className="bd-line-tooltip" style={tooltipStyle(lineTipPos1)}>
          <div className="bd-line-tooltip-date">{monthlyPackageData[hoveredLinePoint].label}</div>
          <div className="bd-line-tooltip-value">{formatValue(monthlyPackageData[hoveredLinePoint].value)}</div>
        </div>,
        portalRoot
      )}
      {portalRoot && hoveredLinePoint2 !== null && yearlyProduceData[hoveredLinePoint2] && createPortal(
        <div className="bd-line-tooltip bd-line-tooltip-green" style={tooltipStyle(lineTipPos2)}>
          <div className="bd-line-tooltip-date">{yearlyProduceData[hoveredLinePoint2].label}</div>
          <div className="bd-line-tooltip-value green">{formatValue(yearlyProduceData[hoveredLinePoint2].value)}</div>
        </div>,
        portalRoot
      )}
    </div>
  );
}

export default BusinessData;
